/**
 * Google Integration OAuth Scopes (Story 4.5, Task 1.2)
 *
 * Maps each Google platform to its required OAuth scope(s) and
 * optional environment binding requirements.
 *
 * PageSpeed does NOT need OAuth — uses API key only (Story 4.11).
 * Login scopes (openid, email, profile) are in @provenleap/auth — NOT here.
 */

import type { Platform } from "@provenleap/db";

// ============================================================================
// Types
// ============================================================================

export interface GoogleScopeConfig {
  /** OAuth 2.0 scope URL(s) required for this platform */
  readonly scopes: readonly string[];
  /** Env binding that must be configured for this platform to work */
  readonly requiresEnvBinding?: string;
}

// ============================================================================
// Scope Mapping
// ============================================================================

/**
 * Maps each Google platform to its required OAuth scope(s).
 *
 * All scopes are "sensitive" — app requires Google's sensitive scope
 * verification (3-5 business days) before production use.
 *
 * google-pagespeed is intentionally excluded — it uses API key auth only.
 */
export const GOOGLE_INTEGRATION_SCOPES: Record<string, GoogleScopeConfig> = {
  "google-analytics-4": {
    scopes: ["https://www.googleapis.com/auth/analytics.readonly"],
  },
  "google-search-console": {
    scopes: ["https://www.googleapis.com/auth/webmasters.readonly"],
  },
  "google-ads": {
    scopes: ["https://www.googleapis.com/auth/adwords"],
    requiresEnvBinding: "GOOGLE_ADS_DEVELOPER_TOKEN",
  },
  "youtube-analytics": {
    scopes: [
      "https://www.googleapis.com/auth/yt-analytics.readonly",
      "https://www.googleapis.com/auth/youtube.readonly",
    ],
  },
  "google-business-profile": {
    scopes: ["https://www.googleapis.com/auth/business.manage"],
  },
} as const;

/** All Google platforms that use OAuth (excludes PageSpeed) */
export const GOOGLE_OAUTH_PLATFORMS = Object.keys(
  GOOGLE_INTEGRATION_SCOPES
) as Platform[];

/** All valid integration scopes as a flat array */
export const ALL_GOOGLE_SCOPES = Object.values(GOOGLE_INTEGRATION_SCOPES)
  .flatMap((config) => config.scopes);

/**
 * Returns the scopes for a given set of platforms.
 * Deduplicates scopes when multiple platforms share a scope.
 */
export function getScopesForPlatforms(platforms: readonly string[]): string[] {
  const scopeSet = new Set<string>();
  for (const platform of platforms) {
    const config = GOOGLE_INTEGRATION_SCOPES[platform];
    if (config) {
      for (const scope of config.scopes) {
        scopeSet.add(scope);
      }
    }
  }
  return [...scopeSet];
}

/**
 * Checks if a scope string is in the allowed integration scopes list.
 */
export function isAllowedScope(scope: string): boolean {
  return ALL_GOOGLE_SCOPES.includes(scope);
}

// ============================================================================
// Zod Schema for postMessage validation
// Delegated to shared multi-provider schema (Story 9.1, Task 1.6)
// ============================================================================

export {
  INTEGRATION_OAUTH_POST_MESSAGE_SCHEMA,
  type IntegrationOAuthPostMessage,
} from "../oauth-post-message";
